/**
 * Benefits administration API.
 *
 * Wraps two backends that share the `/benefits/*` prefix:
 *   * Admin endpoints (plans, periods, dashboard, life-event review,
 *     payroll deductions) — gated MANAGE_EMPLOYEES.
 *   * Employee endpoints (`/benefits/me/...`) — auth only.
 */
import client from "./client";

export type PlanType =
  | "medical"
  | "dental"
  | "vision"
  | "life"
  | "disability_short"
  | "disability_long"
  | "accident"
  | "critical_illness"
  | "hsa"
  | "fsa"
  | "retirement_401k";

export type Tier =
  | "employee_only"
  | "employee_spouse"
  | "employee_children"
  | "family"
  | "waived";

export interface TierRate {
  tier: Tier;
  employer_cost: number;
  employee_cost: number;
}

export interface BenefitPlan {
  id: string;
  name: string;
  carrier_name: string;
  plan_type: PlanType;
  tier_rates: TierRate[];
  details: Record<string, unknown>;
  document_url: string | null;
  document_name: string | null;
  is_active: boolean;
  effective_start: string | null;
  effective_end: string | null;
  created_at: string | null;
}

export interface EnrollmentPeriod {
  id: string;
  name: string;
  period_type: "open" | "new_hire" | "special";
  start_date: string;
  end_date: string;
  eligible_plan_ids: string[];
  auto_notify: boolean;
  employee_id: string | null;
  life_event_id: string | null;
  status: "upcoming" | "active" | "closed";
  created_at: string | null;
}

export interface DependentInput {
  name: string;
  dob?: string;
  ssn?: string;
  relationship?: "spouse" | "child" | "domestic_partner" | "other";
}

export interface BenefitElection {
  id: string;
  employee_id: string;
  enrollment_period_id: string;
  plan_id: string | null;
  tier: Tier;
  dependents: Record<string, unknown>[] | null;
  employee_cost: number;
  employer_cost: number;
  status:
    | "draft"
    | "submitted"
    | "approved"
    | "active"
    | "terminated"
    | "waived";
  elected_at: string | null;
  effective_date: string | null;
  confirmation_pdf_url: string | null;
}

export type LifeEventType =
  | "marriage"
  | "divorce"
  | "birth"
  | "adoption"
  | "death"
  | "loss_of_coverage"
  | "dependent_aging_out";

export interface LifeEvent {
  id: string;
  employee_id: string;
  employee_name?: string | null;
  event_type: LifeEventType;
  event_date: string;
  documentation_url: string | null;
  documentation_name: string | null;
  enrollment_window_end: string | null;
  status: "pending" | "approved" | "denied";
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  notes: string | null;
  created_at: string | null;
}

// ── Admin: plans ───────────────────────────────────────────────────

export async function listPlans(includeInactive = false): Promise<BenefitPlan[]> {
  const res = await client.get<BenefitPlan[]>("/benefits/plans", {
    params: { include_inactive: includeInactive },
  });
  return res.data;
}

export type PlanRequest = Omit<BenefitPlan, "id" | "created_at">;

export async function createPlan(payload: PlanRequest): Promise<BenefitPlan> {
  const res = await client.post<BenefitPlan>("/benefits/plans", payload);
  return res.data;
}

export async function updatePlan(
  id: string,
  payload: Partial<PlanRequest>,
): Promise<BenefitPlan> {
  const res = await client.put<BenefitPlan>(`/benefits/plans/${id}`, payload);
  return res.data;
}

export async function deletePlan(id: string): Promise<void> {
  await client.delete(`/benefits/plans/${id}`);
}

// ── Admin: manual per-employee elections ──────────────────────────

export interface AdminEnrolledElection {
  id: string;
  employee_id: string;
  enrollment_period_id: string | null;
  plan_id: string | null;
  plan_name?: string | null;
  plan_type?: PlanType | null;
  carrier_name?: string | null;
  tier: Tier;
  dependents: Record<string, unknown>[] | null;
  employee_cost: number;
  employer_cost: number;
  status: BenefitElection["status"] | "cobra_continuation";
  elected_at: string | null;
  effective_date: string | null;
  termination_date?: string | null;
  confirmation_pdf_url: string | null;
}

export async function listEmployeeElections(
  employeeId: string,
  includeTerminated = false,
): Promise<AdminEnrolledElection[]> {
  const res = await client.get<AdminEnrolledElection[]>(
    `/benefits/employees/${employeeId}/elections`,
    { params: { include_terminated: includeTerminated } },
  );
  return res.data;
}

export interface AdminElectionRequest {
  plan_id: string;
  tier: Exclude<Tier, "waived">;
  effective_date: string;
  dependents?: DependentInput[];
}

export async function createEmployeeElection(
  employeeId: string,
  payload: AdminElectionRequest,
): Promise<AdminEnrolledElection> {
  const res = await client.post<AdminEnrolledElection>(
    `/benefits/employees/${employeeId}/elections`,
    payload,
  );
  return res.data;
}

export async function terminateEmployeeElection(
  electionId: string,
  termination_date?: string,
): Promise<AdminEnrolledElection> {
  const res = await client.post<AdminEnrolledElection>(
    `/benefits/elections/${electionId}/terminate`,
    null,
    { params: termination_date ? { termination_date } : undefined },
  );
  return res.data;
}

// ── Admin: periods ─────────────────────────────────────────────────

export interface PeriodRequest {
  name: string;
  period_type: "open" | "new_hire" | "special";
  start_date: string;
  end_date: string;
  eligible_plan_ids: string[];
  auto_notify: boolean;
}

export async function listPeriods(
  includeClosed = true,
): Promise<EnrollmentPeriod[]> {
  const res = await client.get<EnrollmentPeriod[]>("/benefits/periods", {
    params: { include_closed: includeClosed },
  });
  return res.data;
}

export async function createPeriod(
  payload: PeriodRequest,
): Promise<EnrollmentPeriod> {
  const res = await client.post<EnrollmentPeriod>("/benefits/periods", payload);
  return res.data;
}

// ── Admin: dashboard + life-events review + payroll ───────────────

export interface PlanBreakdownItem {
  id: string;
  name: string;
  plan_type: PlanType;
  count: number;
}

export interface DashboardNotEnrolled {
  id: string;
  name: string;
  email: string | null;
}

export interface BenefitsDashboard {
  period: {
    id: string;
    name: string;
    status: string;
    start_date: string | null;
    end_date: string | null;
    days_remaining: number | null;
    eligible_count: number;
    enrolled_count: number;
    waived_count: number;
    /** Active employees who've neither elected nor waived yet. */
    not_elected_count: number;
    not_enrolled: DashboardNotEnrolled[];
    plan_breakdown: PlanBreakdownItem[];
  } | null;
  monthly_employer_cost: number;
  monthly_employee_cost: number;
  pending_life_events: number;
}

export async function getBenefitsDashboard(): Promise<BenefitsDashboard> {
  const res = await client.get<BenefitsDashboard>("/benefits/dashboard");
  return res.data;
}

// ── Analytics ────────────────────────────────────────────────────────

export interface BenefitsAnalytics {
  enrollment: {
    eligible: number;
    enrolled: number;
    waived: number;
    not_elected: number;
    enrollment_rate: number;
    waiver_rate: number;
    plan_distribution: {
      plan_id: string | null;
      plan_name: string;
      plan_type: string | null;
      carrier: string | null;
      enrolled: number;
      employer_monthly: number;
      employee_monthly: number;
    }[];
    tier_distribution: { tier: string; label: string; count: number }[];
    total_monthly_premium: number;
    employer_monthly: number;
    employee_monthly: number;
    avg_premium_per_employee: number;
  } | null;
  projections: {
    employer_monthly: number;
    employer_annual_forecast: number;
    total_annual_forecast: number;
    monthly_employer_by_plan: {
      plan_name: string;
      plan_type: string | null;
      employer_monthly: number;
    }[];
    cheapest_mix: {
      employer_monthly_if_all_cheapest: number;
      current_employer_monthly: number;
      potential_monthly_savings: number;
      potential_annual_savings: number;
      note: string;
    };
  } | null;
  demographics: {
    department_patterns: {
      department: string;
      enrolled: number;
      waived: number;
    }[];
    tenure: {
      new_hire_under_1yr: { enrolled: number; waived: number };
      tenured_1yr_plus: { enrolled: number; waived: number };
    };
    age_bands: {
      available: boolean;
      reason?: string;
      bands?: { band: string; count: number }[];
      missing_dob?: number;
    };
    sex_bands: {
      available: boolean;
      reason?: string;
      male?: number;
      female?: number;
      not_recorded?: number;
      missing?: number;
    };
    tier_by_age?: {
      available: boolean;
      bands?: {
        band: string;
        tiers: { tier: string; label: string; count: number }[];
      }[];
    };
  } | null;
  most_popular_plan: { plan_name: string; enrolled: number } | null;
  plan_count: number;
}

export interface RenewalReport {
  report: string;
  generated_on: string;
  enrollment_by_plan: BenefitsAnalytics["enrollment"] extends null
    ? never
    : NonNullable<BenefitsAnalytics["enrollment"]>["plan_distribution"];
  tier_mix: { tier: string; label: string; count: number }[];
  waiver_rate: number;
  enrollment_rate: number;
  total_monthly_premium: number;
  employer_monthly: number;
  year_over_year: Record<string, unknown>;
  demographics: NonNullable<BenefitsAnalytics["demographics"]>;
  data_gaps: string[];
}

export async function getBenefitsAnalytics(): Promise<BenefitsAnalytics> {
  const res = await client.get<BenefitsAnalytics>("/benefits/analytics");
  return res.data;
}

export async function getRenewalReport(): Promise<RenewalReport> {
  const res = await client.get<RenewalReport>(
    "/benefits/analytics/renewal-report",
  );
  return res.data;
}

/** Download the broker renewal packet as an .xlsx (multi-sheet). */
export async function downloadRenewalReportXlsx(): Promise<void> {
  const res = await client.get<Blob>(
    "/benefits/analytics/renewal-report.xlsx",
    { responseType: "blob" },
  );
  const disp = (res.headers?.["content-disposition"] as string) ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disp);
  const filename =
    match?.[1] ?? `renewal-prep-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function listLifeEventsAdmin(
  status?: LifeEvent["status"],
): Promise<LifeEvent[]> {
  const res = await client.get<LifeEvent[]>("/benefits/life-events", {
    params: status ? { status } : undefined,
  });
  return res.data;
}

export async function reviewLifeEvent(
  id: string,
  approve: boolean,
  note?: string,
): Promise<{ life_event: LifeEvent; enrollment_period: EnrollmentPeriod | null }> {
  const res = await client.post(`/benefits/life-events/${id}/review`, {
    approve,
    note,
  });
  return res.data;
}

export async function getPayrollDeductions(
  pay_frequency: "monthly" | "semi_monthly" | "biweekly" | "weekly" = "semi_monthly",
): Promise<{ pay_frequency: string; rows: Record<string, string>[] }> {
  const res = await client.get("/benefits/payroll-deductions", {
    params: { pay_frequency, format: "json" },
  });
  return res.data;
}

export function payrollDeductionsCsvUrl(
  pay_frequency: "monthly" | "semi_monthly" | "biweekly" | "weekly" = "semi_monthly",
): string {
  return `/api/v1/benefits/payroll-deductions?pay_frequency=${pay_frequency}`;
}

// ── Employee: enrollment portal + life-event report ───────────────

export interface EnrollmentContext {
  period: EnrollmentPeriod | null;
  plans: BenefitPlan[];
  elections: BenefitElection[];
  life_events: LifeEvent[];
}

export async function getMyEnrollmentContext(): Promise<EnrollmentContext> {
  const res = await client.get<EnrollmentContext>(
    "/benefits/me/enrollment-context",
  );
  return res.data;
}

export interface ElectionItemPayload {
  plan_id: string | null;
  plan_type: PlanType;
  tier: Tier;
  dependents?: DependentInput[];
}

export async function submitMyElections(
  period_id: string,
  elections: ElectionItemPayload[],
  signature_data?: string,
): Promise<{ elections: BenefitElection[] }> {
  const res = await client.post("/benefits/me/elections", {
    period_id,
    elections,
    signature_data,
  });
  return res.data;
}

export async function reportLifeEvent(payload: {
  event_type: LifeEventType;
  event_date: string;
  documentation_url?: string;
  documentation_name?: string;
  notes?: string;
}): Promise<LifeEvent> {
  const res = await client.post<LifeEvent>(
    "/benefits/me/life-events",
    payload,
  );
  return res.data;
}

// ── Wizard: recommendations ────────────────────────────────────────

export type WizardCoverage =
  | "just_me"
  | "me_spouse"
  | "me_children"
  | "me_family";
export type WizardUsage = "rarely" | "sometimes" | "often";
export type WizardRx = "none" | "one_two" | "three_plus";
export type WizardPriority =
  | "lower_monthly"
  | "predictable"
  | "best_coverage";

export interface WizardAnswers {
  coverage: WizardCoverage;
  usage: WizardUsage;
  rx: WizardRx;
  priority: WizardPriority;
  plan_type?: PlanType;
}

/** Per-plan recommendation returned by the wizard backend. */
export interface PlanRecommendation {
  plan_id: string;
  plan_name: string;
  carrier_name: string;
  plan_type: PlanType;
  tier: Tier;
  monthly_premium: number;
  estimated_annual_cost: number;
  estimated_oop: number;
  reasoning: string;
  fit_score: number;
  details: {
    deductible: number;
    oop_max: number;
    pcp_copay: number;
    specialist_copay: number;
    er_copay: number;
    rx_copay: number;
    coinsurance: number;
    [extra: string]: number | string | null | undefined;
  };
  is_recommended: boolean;
  savings_vs_next: number | null;
}

export type WizardReason =
  | null
  | "no_employee_record"
  | "no_active_period"
  | "no_eligible_plans"
  | "benefits_unavailable";

export interface WizardRecommendationsResponse {
  period: EnrollmentPeriod | null;
  tier: Tier;
  recommendations: PlanRecommendation[];
  reason: WizardReason;
}

export async function getMyRecommendations(
  answers: WizardAnswers,
): Promise<WizardRecommendationsResponse> {
  const res = await client.post<WizardRecommendationsResponse>(
    "/benefits/me/recommendations",
    answers,
  );
  return res.data;
}

// ── HR enrollment reminders ────────────────────────────────────────

export interface RemindResult {
  sent: number;
  skipped: number;
  not_found: number;
  failures: number;
  period_id?: string;
}

/**
 * Nudge employees who haven't elected yet.
 *
 *   - sendEnrollmentReminders()                  → bulk "Remind all" path
 *   - sendEnrollmentReminders([id1, id2])        → per-row "Send reminder"
 *   - sendEnrollmentReminders([id], { periodId }) → scoped to a period
 */
export async function sendEnrollmentReminders(
  employeeIds?: string[],
  opts?: { periodId?: string },
): Promise<RemindResult> {
  const body: Record<string, unknown> = {};
  if (employeeIds && employeeIds.length) body.employee_ids = employeeIds;
  if (opts?.periodId) body.period_id = opts.periodId;
  const res = await client.post<RemindResult>("/benefits/remind", body);
  return res.data;
}

// ── Carrier-portal enrollment export ───────────────────────────────

export type ExportFormatKey =
  | "generic"
  | "blue_cross"
  | "delta_dental"
  | "anthem"
  | "aetna"
  | "uhc";

export interface ExportFormat {
  key: ExportFormatKey | string;
  label: string;
}

export type ExportFileType = "csv" | "xlsx";

export interface ExportEnrollmentOptions {
  format: ExportFormatKey | string;
  file_type: ExportFileType;
  /**
   * Decrypt + emit the FULL SSN. SUPER_ADMIN only — HR_ADMIN gets the
   * masked last-4 even with the flag set (server-side downgrades).
   */
  full_ssn?: boolean;
  period_id?: string;
}

export async function listExportFormats(): Promise<ExportFormat[]> {
  const res = await client.get<{ formats: ExportFormat[] }>(
    "/benefits/export-enrollment/formats",
  );
  return res.data.formats;
}

// ── EDI 834 (Benefit Enrollment & Maintenance) ─────────────────────

/**
 * Envelope + scope parameters for an 834 generation.
 *
 * Everything is optional — the backend stamps sensible placeholders
 * (LENAVIO / CARRIER / GROUP / EMPLOYER) when missing, but the SPA
 * surfaces those loudly so HR fills them in before sending the file
 * to the carrier. Real values come from the carrier's trading-partner
 * setup paperwork.
 */
export interface Edi834Request {
  sender_id?: string;
  receiver_id?: string;
  sender_qualifier?: "ZZ" | "30" | "27";
  receiver_qualifier?: "ZZ" | "30" | "27";
  group_number?: string;
  master_policy_number?: string;
  sponsor_name?: string;
  sponsor_id?: string;
  payer_name?: string;
  /** "T" = test (default), "P" = production. */
  usage_indicator?: "T" | "P";
  /** "00" = original, "RX" = replacement. Default "00". */
  bgn_purpose?: "00" | "RX" | "22" | "15";
  period_id?: string;
  /** Case-insensitive substring match against carrier_name. */
  carrier_filter?: string;
  /** SUPER_ADMIN-only flag; downgraded server-side for HR_ADMIN. */
  full_ssn?: boolean;
}

export interface Edi834PreviewFile {
  carrier_name: string;
  filename: string;
  member_count: number;
  warning_count: number;
}

export interface Edi834PreviewResponse {
  files: Edi834PreviewFile[];
  total_carriers: number;
  total_members: number;
  total_warnings: number;
  uses_placeholders: {
    sender_id: boolean;
    receiver_id: boolean;
    group_number: boolean;
  };
}

export async function preview834Export(
  payload: Edi834Request,
): Promise<Edi834PreviewResponse> {
  const res = await client.post<Edi834PreviewResponse>(
    "/benefits/export-834/preview",
    payload,
  );
  return res.data;
}

export interface Edi834DownloadResult {
  carrierCount: number;
  memberCount: number;
  warningCount: number;
  placeholderCount: number;
  /** ``application/zip`` for bundles, ``application/edi-x12`` for single-carrier downloads. */
  mediaType: string;
}

/**
 * Trigger the 834 generation + download. The backend returns either:
 *   * one ``.txt`` per carrier (when carrier_filter pins to exactly
 *     one), or
 *   * a ``.zip`` bundle with one file per carrier + a manifest.txt.
 */
export async function download834Export(
  payload: Edi834Request,
): Promise<Edi834DownloadResult> {
  const res = await client.post<Blob>("/benefits/export-834", payload, {
    responseType: "blob",
  });

  // Pull filename from Content-Disposition; fall back to a sensible
  // default if the header is stripped by a proxy.
  const disp =
    (res.headers?.["content-disposition"] as string | undefined) ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disp);
  const today = new Date().toISOString().slice(0, 10);
  const filename =
    match?.[1] ??
    ((res.headers?.["content-type"] as string | undefined) ?? "").includes("zip")
      ? `834-bundle-${today}.zip`
      : `834-${today}.txt`;

  const url = URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename as string;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  return {
    carrierCount: parseInt(
      (res.headers?.["x-lenavio-carrier-count"] as string | undefined) ?? "0",
      10,
    ),
    memberCount: parseInt(
      (res.headers?.["x-lenavio-member-count"] as string | undefined) ?? "0",
      10,
    ),
    warningCount: parseInt(
      (res.headers?.["x-lenavio-warning-count"] as string | undefined) ?? "0",
      10,
    ),
    placeholderCount: parseInt(
      (res.headers?.["x-lenavio-placeholder-count"] as string | undefined) ?? "0",
      10,
    ),
    mediaType:
      (res.headers?.["content-type"] as string | undefined) ?? "application/octet-stream",
  };
}

/** Trigger the carrier-export download. Returns the row count + SSN
 *  mode (from response headers) so the SPA can show a precise toast. */
export async function downloadEnrollmentExport(
  opts: ExportEnrollmentOptions,
): Promise<{ rowCount: number; ssnMode: "full" | "masked" }> {
  const params: Record<string, string | boolean> = {
    format: opts.format,
    file_type: opts.file_type,
  };
  if (opts.full_ssn) params.full_ssn = true;
  if (opts.period_id) params.period_id = opts.period_id;

  const res = await client.get<Blob>("/benefits/export-enrollment", {
    params,
    responseType: "blob",
  });

  // Pull filename from Content-Disposition; fall back to a sensible
  // default if the header is stripped by a proxy.
  const disp = (res.headers?.["content-disposition"] as string | undefined) ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disp);
  const filename =
    match?.[1] ??
    `enrollment-${opts.format}-${new Date().toISOString().slice(0, 10)}.${opts.file_type}`;

  // Save the blob via a transient <a download>. The objectURL is
  // revoked right after the click so we don't pin the blob in memory.
  const url = URL.createObjectURL(res.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  const rowCount = parseInt(
    (res.headers?.["x-lenavio-row-count"] as string | undefined) ?? "0",
    10,
  );
  const ssnMode =
    ((res.headers?.["x-lenavio-ssn-mode"] as string | undefined) ?? "masked") ===
    "full"
      ? "full"
      : "masked";
  return { rowCount, ssnMode };
}
